import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {IJmlNode, IJmlSet, JML} from "lib/commons/xml/jml";
import {ISkStructDef} from "lib/edit/schema/schemaAnnots";
import {IEndPoint} from "lib/commons/io/io";
import {ERROR} from "lib/core/errorReport";
import {DOM} from "lib/commons/xml/dom";


/**
 * Source d'une conversion.
 * Une source doit être immutable.
 * Les fonctions utilitaires CONVERT peuvent compléter les propriétés
 * au fur et à mesure des besoins pour une forme résolue.
 *
 * Version 1 : la source est constituée d'un seul noeud (elt, text ou attr). A voir si on veut gérer la conversion
 * d'une séquence de noeuds en une seule cible (N noeuds src -> 1 noeud dst) ou si on le gère uniquement en amont
 * par répétition (N noeuds src == N x (1 noeud src -> 1 noeud dst)).
 */
export interface IConvertSrc {

	/** Définition de la struct cible attendue par cette conversion. */
	outStruct: ISkStructDef

	/** Source non résolue : doc + xa */
	xa?: IXAddr
	//seq?:IXAddrSeq A voir si utile...
	doc?: Document

	/** Source résolue au format dom */
	node?: Element | Text | Attr /*| DocumentFragment*/

	/** Source résolue au format jml (format impossible pour un attribut). */
	jml?: IJmlSet
}

/**
 * Résultat d'une transformation
 */
export interface IConvertDst {

	/** Résultat d'une transformation au format dom */
	node?: Element | Text | Attr | DocumentFragment

	/** Résultat d'une transformation au format jml (format impossible pour un attribut). */
	jml?: IJmlSet
}

/**
 * Convertisseur
 */
export interface IConverter {
	convert(src: IConvertSrc): Promise<IConvertDst | null>
}

/**
 * Fournisseur de convertisseur.
 */
export interface IConverterProvider {
	findConverter(src: IConvertSrc): IConverter | null
}

/** Définition d'un méta-modèle. */
export class ConverterLib {
	protected _dict: Map<string, (elt: Element) => IConverter> = new Map();

	registerConverterFromXml(key: string, factory: (elt: Element) => IConverter) {
		this._dict.set(key, factory);
	}

	getConverterFromXml(key: string, elt: Element): IConverter {
		const factory = this._dict.get(key);
		return factory ? factory(elt) : null;
	}
}

/** Librairie de convertisseurs. */
export const CONVERTERLIB = new ConverterLib();


/**
 * Fournisseur de converters pour un contexte cible donné et cohérent.
 * Ex: set de parts d'une CompoPrim.
 */
export class ConverterProv implements IConverterProvider {
	/** Conversions bi-directionnelles par simple switch du tag root. */
	switchTagRoots?: Set<string>[];

	/** Conversions bi-directionnelles avec un converter custom. */
	customSwitch?: { cnv: IConverter, tags: Set<string> }[];

	/** Converters custom pour une source (nodeName source) et une cible donnée (structName). */
	converters: Map<string/* nodeName root src*/, Map<string/* structName target*/, IConverter>>;

	addSwitch(...tagNames: string[]): this {
		if (!this.switchTagRoots) this.switchTagRoots = [];
		this.switchTagRoots.push(new Set(tagNames));
		return this;
	}

	addCustomSwitch(cnv: IConverter, ...tagNames: string[]): this {
		if (!this.customSwitch) this.customSwitch = [];
		this.customSwitch.push({cnv, tags: new Set(tagNames)});
		return this;
	}

	addConverter(srcNodeName: string, dstStructName: string, cnv: IConverter): this {
		if (!this.converters) this.converters = new Map();
		let dstMap = this.converters.get(srcNodeName);
		if (!dstMap) {
			dstMap = new Map();
			this.converters.set(srcNodeName, dstMap);
		}
		dstMap.set(dstStructName, cnv);
		return this;
	}

	addConverters(srcNodeNames: string[], dstStructNames: string[], cnv: IConverter): this {
		if (!this.converters) this.converters = new Map();
		for (const srcName of srcNodeNames) {
			let dstMap = this.converters.get(srcName);
			if (!dstMap) {
				dstMap = new Map();
				this.converters.set(srcName, dstMap);
			}
			for (const dstName of dstStructNames) dstMap.set(dstName, cnv);
		}
		return this;
	}

	findConverter(src: IConvertSrc): IConverter | null {
		const name = CONVERT.getNameRoot(src);
		if (this.converters) {
			const targets = this.converters.get(name);
			if (targets) {
				const cnv = targets.get(src.outStruct.structName);
				if (cnv) return cnv;
			}
		}
		if (this.customSwitch) for (const conf of this.customSwitch) {
			if (conf.tags.has(name) && conf.tags.has(src.outStruct.structName)) return conf.cnv;
		}
		if (this.switchTagRoots) for (const tags of this.switchTagRoots) {
			if (tags.has(name) && tags.has(src.outStruct.structName)) return SwitchTagRootConverter.SINGLETON;
		}
		return null;
	}
}

/**
 * Liste de IConvertProvider : le 1er IConvertProvider qui trouve un IConverter l'emporte.
 */
export class SetConverterProv extends Array<IConverterProvider> implements IConverterProvider {

	findConverter(src: IConvertSrc): IConverter | null {
		for (const prov of this) {
			const c = prov.findConverter(src);
			if (c) return c;
		}
		return null;
	}
}


export class SwitchTagRootConverter implements IConverter {
	static SINGLETON = new SwitchTagRootConverter();

	async convert(src: IConvertSrc): Promise<IConvertDst | null> {
		if (!src.jml) {
			if (CONVERT.getSrcAsNode(src) instanceof Attr) {
				throw Error("TODO SwitchTagRootConverter for attr");
			}
			src.jml = [];
			JML.domNode2jml(src.node, src.jml);
		}
		const newC = [] as IJmlSet;
		src.outStruct.createContent(newC);
		const res = src.jml.concat(); //on duplique uniquement le tableau racine src.jml (on part du principe que les sous-objets ne sont pas modifiés, source immutable)
		res[0] = Object.assign({}, res[0], newC[0]); //on remplace le tag racine et ses éventuels attributs identifiant (role...).
		return {jml: res};
	}
}


export class XslConverter implements IConverter {

	xslt: XSLTProcessor;

	params: Map<string, any>;

	constructor(public xslPath: IEndPoint) {}

	addParam(name: string, value: any) {
		if (!this.params) this.params = new Map();
		this.params.set(name, value);
	}

	async convert(src: IConvertSrc): Promise<IConvertDst | null> {
		if (!this.xslt) {
			try {
				const xsl = await this.xslPath.fetchDom();
				this.xslt = new XSLTProcessor();
				this.xslt.importStylesheet(xsl);
			} catch (e) {
				ERROR.log("Fail to load xsl: " + this.xslPath.url, e);
				this.xslt = null;
			}
		}
		if (this.xslt) {
			try {
				this.xslt.setParameter("", "targetTagName", src.outStruct.structName);
				if (this.params) for (const [k, v] of this.params) {
					this.xslt.setParameter("", k, v);
				}
				const doc = this.xslt.transformToDocument(CONVERT.getSrcAsNode(src));

				if (doc.documentElement.localName === "fragment" && doc.documentElement.namespaceURI === DOM.SCCORE_NS) {
					const frag = doc.createDocumentFragment();
					const root = doc.documentElement;
					while (root.hasChildNodes()) frag.appendChild(root.firstChild);
					return {node: frag};
				}
				return {node: doc.documentElement};
			} catch (e) {
				ERROR.log("Fail to execute xsl: " + this.xslPath.url, e);
			} finally {
				//this.xslt.reset();
			}
		}
		return {node: src.node, jml: src.jml};
	}
}

export namespace CONVERT {

	export function getNameRoot(src: IConvertSrc): string {
		if (src.jml) return JML.jmlNode2name(src.jml[0] as IJmlNode);
		if (!src.node) src.node = XA.findDomLast(src.xa, src.doc) as Element | Text | Attr;
		return src.node instanceof Attr ? '@' + src.node.nodeName : src.node.nodeName;
	}

	export function getSrcAsNode(src: IConvertSrc): Element | Text | Attr {
		if (src.node) return src.node;
		if (src.doc && src.xa) src.node = XA.findDomLast(src.xa, src.doc) as Element | Text | Attr;
		else if (src.jml) src.node = JML.jmlToDom(src.jml).documentElement;
		return src.node;
	}

	export function getDstAsJml(dst: IConvertDst | null): IJmlSet | null {
		if (!dst) return null;
		if (dst.jml) return dst.jml;
		if (!dst.node) return null;
		dst.jml = [];
		JML.domNode2jml(dst.node, dst.jml);
		return dst.jml;
	}

	export function getDstAsNode(dst: IConvertDst | null): Element | Text | Attr | DocumentFragment | null {
		if (!dst) return null;
		if (dst.node) return dst.node;
		return dst.jml ? JML.jmlToDom(dst.jml).documentElement : null;
	}
}

// Swith de tag racine XML AVEC préservation des @ (les @vides (<=> @ requis) ne remplacent pas les @ courants)
export class SwitchEltConverter implements IConverter {
	async convert(src: IConvertSrc): Promise<IConvertDst | null> {
		if (!src.jml) {
			src.jml = [];
			JML.domNode2jml(src.node, src.jml);
		}
		const newC = [] as IJmlSet;
		src.outStruct.createContent(newC);
		const res = src.jml.concat(); //on duplique uniquement le tableau racine src.jml (on part du principe que les sous-objets ne sont pas modifiés, source immutable)
		const tag = res[0] as any;
		const newTag = newC[0] as any;
		for (const [key, value] of Object.entries(newTag)) {
			if(!value && key in tag)
				newTag[key] = tag[key]
		}
		res[0] = Object.assign({}, tag, newTag); //on remplace le tag racine et ses éventuels attributs si non nuls
		return {jml: res};
	}
}
CONVERTERLIB.registerConverterFromXml("eltWithAtts", () => new SwitchEltConverter());